/* eslint-disable import/no-cycle */
import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';
import {
  CertificateStatus,
  PersonalCertificateResultV2,
} from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import { ApplicationType } from '@sdir/httpclient/lib/clients/aps/application';
import { ActionCard } from '../../Molecules';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

type CertificateActionsProps = {
  certificate: PersonalCertificateResultV2;
  isValidForRenewal?: boolean;
  isValidForDuplicate?: boolean;
};

export const CertificateActions: FC<CertificateActionsProps> = ({
  certificate,
  isValidForRenewal = false,
  isValidForDuplicate = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();

  const { showInactiveInfo, showRenewalOption, validFrom } = useMemo(() => {
    const isInactive = certificate?.status === CertificateStatus.Inactive;
    const validFromDate = certificate.validFromDate ? new Date(certificate.validFromDate) : null;
    const validFromString = validFromDate ? moment(validFromDate).format('DD.MM.YYYY') : null;
    return {
      showInactiveInfo: isInactive,
      showRenewalOption: isValidForRenewal,
      validFrom: validFromString,
    };
  }, [certificate, isValidForRenewal, isValidForDuplicate]);

  const openNewApplication = useCallback(
    (appType: ApplicationType, certificateCode: string) => {
      navigate({
        pathname: routes.newApplication,
        search: `?type=${appType}&certificate=${certificateCode}`,
      });
    },
    [navigate]
  );

  const handleApplyForRenewal = useCallback(() => {
    const certType = certificate.certificateType || '';
    const type = !certType || certType.includes('renewal') ? certType : `${certType}_renewal`;
    openNewApplication(ApplicationType.Renewal, type);
  }, [certificate.certificateType, openNewApplication]);

  return (
    <ActionsWrapper>
      {showInactiveInfo && (
        <ActionCard heading={intl.formatMessage({ id: 'certificate.actions.inactive.heading' })}>
          {`${intl.formatMessage({ id: 'certificate.actions.inactive.description' })}${validFrom}`}
        </ActionCard>
      )}
      {showRenewalOption && (
        <ActionCard
          heading={intl.formatMessage({ id: 'certificate.actions.renewal.heading' })}
          actionText={intl.formatMessage({ id: 'certificate.actions.renewal.actiontext' })}
          onClick={handleApplyForRenewal}
        >
          {intl.formatMessage({ id: 'certificate.actions.renewal.description' })}
          <a
            target="_blank"
            rel="noreferrer"
            // eslint-disable-next-line max-len
            href="https://www.sdir.no/sjofart/sjofolk/sertifikater/sok-personlig-maritimt-sertifikat/saksbehandlingstid-for-personlige-sertifikater/"
          >
            {intl.formatMessage({ id: 'certificate.actions.renewal.link' })}
          </a>
        </ActionCard>
      )}
    </ActionsWrapper>
  );
};

const ActionsWrapper = styled.div`
  > * {
    margin-bottom: 24px;
  }
`;
